import { Post } from "../models/Post";
import { PendingRequest, PendingSource } from "./pendingGeneration";
import {
  compareDraftTimelinePosts,
  draftPaginationUnitKeys,
  draftTimelineAt,
  LEGACY_ROW_POST_LIMIT,
  MANUAL_ROW_POST_LIMIT,
} from "./draftTimeline";

export type PendingGenerationProgress = {
  stage?: "reading_sources" | "creating_posts";
  sourcesProcessed?: number;
  processedSourceIndices?: number[];
  processedSourceIds?: string[];
  initiatedPostCountsBySourceId?: Record<string, number>;
  status?: string;
};

export type DraftSourceGroup = {
  key: string;
  sourceId?: string;
  label: string;
  sourceType: PendingSource["type"] | "manual";
  sourceUrl?: string;
  favicon?: string;
  /** Legacy URL posts predate persisted title/favicon metadata. */
  needsSourceMetadata: boolean;
  generatedAt: number;
  posts: Post[];
  isPreparing: boolean;
  pendingStartedAt?: number;
  /** The composer's per-source cap: how many placeholder cards to show. */
  expectedPosts?: number;
  stage?: PendingGenerationProgress["stage"];
  isConfirming?: boolean;
  failedRequest?: { requestId: string; message: string; errorCode?: PendingRequest["errorCode"]; suggestion?: string; fallback?: PendingRequest["fallback"] };
};

/** Publishing failures belong in the publishing views; generation failures stay here. */
export function isDraftGalleryPost(post: Pick<Post, "status" | "generationError">): boolean {
  return post.status === "draft" || post.status === "generating" ||
    (post.status === "failed" && Boolean(post.generationError));
}

/** The source placeholder is replaced by post cards as soon as any arrive. */
export function showSourcePlaceholder(group: DraftSourceGroup): boolean {
  return group.isPreparing && !group.isConfirming && group.posts.length === 0;
}

const TERMINAL_REQUEST_STATUSES = new Set(["completed", "empty", "failed"]);

function normalizeSource(source?: string | null): string {
  return source?.trim() || "unknown";
}

function requestSourceKey(
  requestId: string,
  generationSource?: string | null,
  sourceId?: string,
): string {
  if (sourceId) {
    return `request:${requestId}:source-id:${normalizeSource(sourceId)}`;
  }
  return `request:${requestId}:source:${normalizeSource(generationSource)}`;
}

function postGroupKey(
  post: Post,
  paginationUnits: ReadonlyMap<string, string>,
): string {
  if (post.generationSource === null) {
    return `manual:${post.id}`;
  }
  if (post.generationRequestId) {
    return requestSourceKey(
      post.generationRequestId,
      post.generationSource,
      post.generationSourceId,
    );
  }
  if (
    post.generationSource &&
    normalizeSource(post.generationSource) !== "unknown"
  ) {
    // Request-less posts predate persisted generation identity. Gap-based,
    // bounded sessions preserve placeholders created together without either
    // a wall-clock boundary split or one unbounded row per repeated URL.
    return paginationUnits.get(post.id) || `source:${post.id}`;
  }
  // Historical source-less posts predate the explicit manual marker. Their
  // provenance cannot be recovered, so present them with manual drafts rather
  // than fragmenting the gallery into one ambiguous row per post.
  return `manual:${post.id}`;
}

function directSourceUrl(source?: string | null): string | undefined {
  const normalized = source?.trim();
  if (!normalized || normalized.includes(" + ")) return undefined;
  try {
    const url = new URL(normalized);
    return url.protocol === "http:" || url.protocol === "https:"
      ? url.toString()
      : undefined;
  } catch {
    return undefined;
  }
}

function sourcePresentation(generationSource?: string | null): {
  label: string;
  sourceType: DraftSourceGroup["sourceType"];
  sourceUrl?: string;
} {
  if (generationSource === null) {
    return { label: "Manually created", sourceType: "manual" };
  }

  const source = normalizeSource(generationSource);
  if (
    source === "unknown" ||
    source === "direct-text-input" ||
    source === "text-input"
  ) {
    return source === "unknown"
      ? { label: "Manually created", sourceType: "manual" }
      : { label: "Pasted text", sourceType: "text" };
  }

  if (source.includes(" + ")) {
    const count = source.split(" + ").length;
    return {
      label: `${count} sources combined`,
      sourceType: "url",
    };
  }

  try {
    const url = new URL(source);
    const isYoutube =
      url.hostname === "youtu.be" || url.hostname.endsWith("youtube.com");
    return {
      label: url.hostname.replace(/^www\./, ""),
      sourceType: isYoutube ? "youtube" : "url",
      sourceUrl: url.toString(),
    };
  } catch {
    return { label: source, sourceType: "text" };
  }
}

/**
 * Builds the Drafts page's display model. A source row is identified by the
 * generation request plus source, so re-generating the same article produces
 * a distinct, correctly time-sorted row. Legacy posts without a request ID use
 * a stable source/time window. Rows follow the gallery query's immutable
 * creation order, so editing a post cannot move its row. Manual drafts are
 * batched when adjacent in that creation timeline, capped at 12 cards so a
 * long source-less history cannot make pagination scan without bound.
 *
 * Pending sources are merged into the same key before their post documents
 * arrive. Once Firestore emits those posts, the row keeps its identity and the
 * UI can replace ghost slots in place rather than removing one shelf and
 * inserting an unrelated card grid.
 */
export function buildDraftSourceGroups(
  posts: Post[],
  pendingRequests: PendingRequest[],
  pendingProgress: Record<string, PendingGenerationProgress>,
): DraftSourceGroup[] {
  const groups = new Map<string, DraftSourceGroup>();
  const paginationUnits = draftPaginationUnitKeys(posts);
  const requestKeys = new Map<string, string[]>();
  const requestSourceKeys = new Map<string, Map<string, string[]>>();

  for (const post of posts) {
    const key = postGroupKey(post, paginationUnits);
    const existing = groups.get(key);
    const presentation = sourcePresentation(post.generationSource);
    const isSynthesisSource = post.generationSourceId === "synthesis";
    if (existing) {
      existing.posts.push(post);
      existing.generatedAt = Math.max(
        existing.generatedAt,
        draftTimelineAt(post),
      );
    } else {
      groups.set(key, {
        key,
        sourceId: post.generationSourceId,
        ...presentation,
        label: post.generationSourceLabel || presentation.label,
        sourceType: post.generationSourceType || presentation.sourceType,
        sourceUrl: isSynthesisSource ? undefined : presentation.sourceUrl,
        favicon: post.generationSourceFavicon,
        needsSourceMetadata: Boolean(
          !isSynthesisSource &&
            presentation.sourceUrl &&
            !post.generationSourceLabel,
        ),
        generatedAt: draftTimelineAt(post),
        posts: [post],
        isPreparing: false,
      });
    }

    if (post.generationRequestId) {
      const keys = requestKeys.get(post.generationRequestId) || [];
      if (!keys.includes(key)) keys.push(key);
      requestKeys.set(post.generationRequestId, keys);

      const sourceKeys =
        requestSourceKeys.get(post.generationRequestId) || new Map();
      const normalizedGenerationSource = normalizeSource(post.generationSource);
      const matchingKeys = sourceKeys.get(normalizedGenerationSource) || [];
      if (!matchingKeys.includes(key)) matchingKeys.push(key);
      sourceKeys.set(normalizedGenerationSource, matchingKeys);
      requestSourceKeys.set(post.generationRequestId, sourceKeys);
    }
  }

  for (const request of pendingRequests) {
    const progress = pendingProgress[request.clientRequestId];
    const requestIsTerminal = TERMINAL_REQUEST_STATUSES.has(
      progress?.status || "",
    );
    const existingRequestKeys = requestKeys.get(request.clientRequestId) || [];
    const existingSourceKeys = requestSourceKeys.get(request.clientRequestId);

    const accepted = progress?.status === "processing" || progress?.status === "completed";
    const terminalFailure = progress?.status === "failed" || progress?.status === "empty";
    const requestError = accepted ? undefined : request.error ||
      (terminalFailure && request.confirming ? "We couldn’t prepare posts from this input." : undefined);
    // Batch errors belong to the request, not to each source. Keep any posts
    // already present and add one recovery row listing the affected inputs.
    if (requestError && request.sources.length > 1) {
      groups.set(`request:${request.clientRequestId}:failure`, {
        key: `request:${request.clientRequestId}:failure`,
        label: request.sources.map(source => source.label).join(" · "),
        sourceType: "manual", needsSourceMetadata: false,
        generatedAt: request.clickedAt, posts: [], isPreparing: false,
        failedRequest: { requestId: request.clientRequestId, message: requestError, errorCode: request.errorCode, suggestion: request.suggestion, fallback: request.fallback },
      });
      continue;
    }

    request.sources.forEach((source, sourceIndex) => {
      let key = source.generationSource
        ? requestSourceKey(
            request.clientRequestId,
            source.generationSource,
            source.sourceId,
          )
        : source.sourceId
          ? requestSourceKey(
              request.clientRequestId,
              undefined,
              source.sourceId,
            )
          : undefined;

      // During a rolling app/server release, one side may know sourceId while
      // the other only knows generationSource. Alias only when the match is
      // unambiguous so duplicate URLs/text remain separate.
      if (!key || !groups.has(key)) {
        const normalizedGenerationSource = normalizeSource(
          source.generationSource,
        );
        const matchingExistingKeys =
          existingSourceKeys?.get(normalizedGenerationSource) || [];
        const matchingPendingSourceCount = request.sources.filter(
          (pendingSource) =>
            normalizeSource(pendingSource.generationSource) ===
            normalizedGenerationSource,
        ).length;
        if (
          matchingExistingKeys.length === 1 &&
          matchingPendingSourceCount === 1
        ) {
          key = matchingExistingKeys[0];
        }
      }

      // A single source can also be joined unambiguously when an older marker
      // omitted generationSource, or a synthesis source changed after a
      // partial extraction failure.
      if (
        (!key || !groups.has(key)) &&
        request.sources.length === 1 &&
        existingRequestKeys.length === 1
      ) {
        key = existingRequestKeys[0];
      }
      key ||= `request:${request.clientRequestId}:pending:${sourceIndex}`;

      const existing = groups.get(key);
      const isConfirming = Boolean(request.confirming && !accepted && !requestError && !existing?.posts.length);

      // If progress is unavailable, the presence of a post is enough evidence
      // that this source has moved past its pre-placeholder wait.
      const processed =
        source.sourceId && Array.isArray(progress?.processedSourceIds)
          ? progress.processedSourceIds.includes(source.sourceId)
          : progress?.processedSourceIndices
            ? progress.processedSourceIndices.includes(sourceIndex)
            : Boolean(existing?.posts.length);
      const initiatedPostCounts = progress?.initiatedPostCountsBySourceId;
      let initiatedPostCount = source.sourceId
        ? initiatedPostCounts?.[source.sourceId]
        : initiatedPostCounts?.[`source-${sourceIndex}`];
      if (
        initiatedPostCount === undefined &&
        request.sources.length === 1 &&
        initiatedPostCounts
      ) {
        const counts = Object.values(initiatedPostCounts);
        if (counts.length === 1) initiatedPostCount = counts[0];
      }
      // A completed source with no post documents is either an extraction
      // failure, out of budget, or produced no usable topics. Retire its ghost
      // only when the server explicitly reports zero. A positive count means
      // the posts listener is lagging the separate progress listener, so keep
      // the ghost until those documents arrive.
      if (
        !existing && !requestError &&
        (initiatedPostCount === 0 ||
          (initiatedPostCount === undefined &&
            (requestIsTerminal || processed)))
      ) {
        return;
      }
      const isWaitingForPosts =
        initiatedPostCount !== undefined &&
        (existing?.posts.length || 0) < initiatedPostCount;
      const isPreparing =
        !requestError && (isWaitingForPosts || (!requestIsTerminal && !processed));

      if (existing) {
        const isSynthesisGroup =
          existing.sourceId === "synthesis" || source.sourceId === "synthesis";
        if (!isSynthesisGroup || existing.needsSourceMetadata) {
          existing.label = source.label || existing.label;
          existing.sourceType = source.type;
        }
        existing.sourceUrl =
          existing.sourceUrl ||
          (isSynthesisGroup
            ? undefined
            : directSourceUrl(source.generationSource));
        existing.favicon = source.favicon || existing.favicon;
        existing.needsSourceMetadata = false;
        existing.generatedAt = Math.max(
          existing.generatedAt,
          request.clickedAt,
        );
        existing.isPreparing = isPreparing;
        existing.isConfirming = isConfirming;
        existing.failedRequest = requestError ? { requestId: request.clientRequestId, message: requestError, errorCode: request.errorCode, suggestion: request.suggestion, fallback: request.fallback } : undefined;
        existing.pendingStartedAt = request.clickedAt;
        existing.expectedPosts = request.maxPostsPerSource;
        existing.stage = progress?.stage;
      } else {
        groups.set(key, {
          key,
          sourceId: source.sourceId,
          label: source.label,
          sourceType: source.type,
          sourceUrl:
            source.sourceId === "synthesis"
              ? undefined
              : directSourceUrl(source.generationSource),
          favicon: source.favicon,
          needsSourceMetadata: false,
          generatedAt: request.clickedAt,
          posts: [],
          isPreparing,
          isConfirming,
          pendingStartedAt: request.clickedAt,
          expectedPosts: request.maxPostsPerSource,
          stage: progress?.stage,
          failedRequest: requestError ? { requestId: request.clientRequestId, message: requestError, errorCode: request.errorCode, suggestion: request.suggestion, fallback: request.fallback } : undefined,
        });
      }
    });
  }

  const sortedGroups = Array.from(groups.values())
    // Failed results still count above when settling a request, but don't leave
    // failed cards or empty source rows in Drafts or Recent drafts.
    .map((group) => ({
      ...group,
      posts: group.posts.filter((post) => post.status !== "failed"),
    }))
    .filter((group) => group.posts.length > 0 || group.isPreparing || group.isConfirming || group.failedRequest)
    .flatMap((group) => {
      if (
        !group.key.startsWith("source:") ||
        group.posts.length <= LEGACY_ROW_POST_LIMIT
      ) {
        return [group];
      }

      const chronologicalPosts = [...group.posts].sort(
        compareDraftTimelinePosts,
      );
      const chunks: DraftSourceGroup[] = [];
      for (
        let index = 0;
        index < chronologicalPosts.length;
        index += LEGACY_ROW_POST_LIMIT
      ) {
        const posts = chronologicalPosts.slice(
          index,
          index + LEGACY_ROW_POST_LIMIT,
        );
        chunks.push({
          ...group,
          key: `${group.key}:chunk:${index / LEGACY_ROW_POST_LIMIT}`,
          generatedAt: Math.max(...posts.map(draftTimelineAt)),
          posts,
        });
      }
      return chunks;
    })
    .map((group) => ({
      ...group,
      posts: [...group.posts].sort(
        (a, b) => b.updatedAt - a.updatedAt || b.id.localeCompare(a.id),
      ),
    }))
    .sort((a, b) => {
      const aNewestPostId = a.posts.reduce(
        (latestId, post) =>
          draftTimelineAt(post) === a.generatedAt && post.id > latestId
            ? post.id
            : latestId,
        "",
      );
      const bNewestPostId = b.posts.reduce(
        (latestId, post) =>
          draftTimelineAt(post) === b.generatedAt && post.id > latestId
            ? post.id
            : latestId,
        "",
      );
      const aTieBreak = aNewestPostId || a.key;
      const bTieBreak = bNewestPostId || b.key;
      if (
        a.generatedAt === b.generatedAt &&
        a.posts.length === 0 &&
        b.posts.length === 0
      ) {
        return a.key.localeCompare(b.key);
      }
      return (
        b.generatedAt - a.generatedAt || bTieBreak.localeCompare(aTieBreak)
      );
    });

  return sortedGroups.reduce<DraftSourceGroup[]>((batchedGroups, group) => {
    const previousGroup = batchedGroups[batchedGroups.length - 1];
    if (
      group.sourceType === "manual" &&
      previousGroup?.sourceType === "manual" &&
      previousGroup.posts.length < MANUAL_ROW_POST_LIMIT
    ) {
      previousGroup.posts.push(...group.posts);
      previousGroup.posts.sort(
        (a, b) => b.updatedAt - a.updatedAt || b.id.localeCompare(a.id),
      );
      return batchedGroups;
    }

    batchedGroups.push(
      group.sourceType === "manual"
        ? { ...group, key: `manual-batch:${group.key}` }
        : group,
    );
    return batchedGroups;
  }, []);
}

/** Studio shows generated drafts from the viewer's local calendar day. */
export function buildDraftSourceGroupsForDay(
  posts: Post[],
  requests: PendingRequest[],
  progress: Record<string, PendingGenerationProgress>,
  day: Date,
): DraftSourceGroup[] {
  const start = new Date(day).setHours(0, 0, 0, 0);
  const end = new Date(day).setHours(24, 0, 0, 0);
  const inDay = (timestamp: number) => timestamp >= start && timestamp < end;
  return buildDraftSourceGroups(
    posts.filter((post) => inDay(post.createdAt)),
    requests.filter((request) => inDay(request.clickedAt)),
    progress,
  ).filter(
    (group) => group.sourceType !== "manual" && inDay(group.generatedAt),
  );
}
